import { assert_number_gte_0, assert_number_lte, assert_string_not_empty, assert_type_boolean, assert_type_enum } from '@shared/asserts/utils_asserts';
import { DTO__grid2d_cell } from '@shared/dto/grid2d_cell/DTO__grid2d_cell';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { ENUM__workspace_entity } from '../+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { DTO__database_entity } from '../_database_entity/DTO__database_entity';
import type { DTO__grid2d_chunk } from './DTO__grid2d_chunk';

//grid2d contains unique list of cells by hash_sha256_content
export class DTO__grid2d_chunk_generic<T extends DTO__grid2d_cell> extends DTO__database_entity {
  static _class_name_static: string = ENUM__dto_class_name.ABSTRACT;

  /*
   * STATIC FIELDS
   */

  static KEY_ARE_CELLS_LOADED_FIRST_TIME: keyof DTO__grid2d_chunk = 'are_cells_loaded_first_time';
  static KEY_CELLS_LIST: keyof DTO__grid2d_chunk = 'cells_list';
  static KEY_GRID2D_ID: keyof DTO__grid2d_chunk = 'grid2d_id';
  static KEY_ENTITY_TYPE: keyof DTO__grid2d_chunk = 'entity_type';

  /*
   * STATIC METHODS
   */

  static sort_by_chunk_index(a: DTO__grid2d_chunk, b: DTO__grid2d_chunk) {
    return a.chunk_index - b.chunk_index;
  }

  /*
   * DB FIELDS
   */

  workspace_id!: string;
  entity_type!: string;

  grid2d_id!: string;
  chunk_index!: number;

  cells_amount!: number;
  images_amount!: number;

  /*
   * SERVER RUNTIME FIELDS
   */
  are_cells_loaded_first_time!: boolean;

  //cells sorted by updated_at in reverse order
  cells_list: T[];

  /*
   * CLIENT RUNTIME FIELDS
   */

  cells_amount_before_this_chunk!: number;

  /*
   * METHODS
   */

  constructor() {
    super();

    this.are_cells_loaded_first_time = false;

    this.cells_list = [];
  }

  assert_standard_fields(): void {
    super.assert_standard_fields();

    assert_string_not_empty(this.workspace_id);
    assert_type_enum(this.entity_type, ENUM__workspace_entity);

    assert_string_not_empty(this.grid2d_id);
    assert_number_gte_0(this.chunk_index);
    assert_number_gte_0(this.cells_amount);
    assert_number_gte_0(this.images_amount);

    assert_type_boolean(this.are_cells_loaded_first_time);

    assert_number_gte_0(this.cells_amount_before_this_chunk);
  }

  get_cell_by_index(cell_index_absolute: number): T {
    let cell_index_relative = cell_index_absolute - this.cells_amount_before_this_chunk;

    assert_number_lte(0, cell_index_relative);
    assert_number_lte(cell_index_relative, this.cells_amount - 1);

    let out = this.create_stub_cell();

    if (!this.are_cells_loaded_first_time) return out; //not loaded yet
    if (this.cells_list.length <= cell_index_relative) return out; //cell not upserted

    out = this.cells_list[cell_index_relative];

    return out;
  }

  create_stub_cell(): T {
    return DTO__grid2d_cell.CREATE_STUB() as T;
  }

  upsert_cell(cell: T) {
    let cell_index = this.cells_list.findIndex(item => item.hash_sha256_content == cell.hash_sha256_content);

    if (cell_index == -1) {
      this.cells_list = [cell, ...this.cells_list];
    } else {
      this.cells_list[cell_index] = cell;
    }

    this.cells_list.sort(DTO__database_entity.sort_by_updated_at_newest_first);

    //recreate to rerender
    this.cells_list = [...this.cells_list];
  }

  delete_cell(cell_target: T) {
    this.cells_list = this.cells_list.filter(cell_saved => cell_saved.hash_sha256_content != cell_target.hash_sha256_content);
  }
}
